import { Controller } from "react-hook-form";
// ** Third Party
import PropTypes from "prop-types";
import classnames from "classnames";

// A radio group rendered as selectable cards, for the handful of choices that
// deserve an icon and a line of explanation rather than a <select> row
// (payment model at brand setup, ...). Same contract as the other field
// wrappers: hand it `control`, `name` and `errors` and it owns the rest.
const columnClasses = {
    1: "sm:grid-cols-1",
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-3"
};

const OptionCardField = (props) => {
    const {
        control,
        name,
        options = [],
        errors,
        label = "",
        helpText = "",
        required = false,
        columns = 2,
        disabled = false,
        className = "",
        customOnChange = () => {}
    } = props;

    const message = errors && errors[name]?.message;

    return (
        <div className={classnames("mb-3", { [className]: className })}>
            {label && (
                <label htmlFor={name} className="text-sm font-semibold text-gray-900">
                    {label}
                    {required && <span className="font-bold text-danger">*</span>}
                </label>
            )}
            {helpText && <p className="mt-1 text-sm text-muted">{helpText}</p>}

            <Controller
                control={control}
                name={name}
                render={({ field: { value, onChange } }) => (
                    <div
                        role="radiogroup"
                        aria-label={label || name}
                        className={classnames("mt-3 grid gap-4", columnClasses[columns] || columnClasses[2])}>
                        {options.map((option) => {
                            const Icon = option.icon;
                            const selected = value === option.value;
                            const isDisabled = disabled || option.disabled;

                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    role="radio"
                                    aria-checked={selected}
                                    disabled={isDisabled}
                                    data-cy={`${name}-${option.value}`}
                                    onClick={() => {
                                        onChange(option.value);
                                        customOnChange(option.value);
                                    }}
                                    className={classnames(
                                        "rounded-xl border p-5 text-left transition disabled:cursor-not-allowed disabled:opacity-60",
                                        selected
                                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                                            : "border-gray-300 bg-white hover:border-accent"
                                    )}>
                                    {Icon && (
                                        <Icon className={classnames("h-6 w-6", selected ? "text-primary" : "text-muted")} />
                                    )}
                                    <p className={classnames("text-base font-semibold text-gray-900", { "mt-4": Icon })}>
                                        {option.title || option.label}
                                    </p>
                                    {option.description && (
                                        <p className="mt-2 text-sm leading-relaxed text-muted">{option.description}</p>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            />

            {message && (
                <small className="mt-2 block text-danger" data-cy={`error-${name}`}>
                    {message}
                </small>
            )}
        </div>
    );
};

OptionCardField.propTypes = {
    control: PropTypes.any.isRequired,
    name   : PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value      : PropTypes.string.isRequired,
            title      : PropTypes.string,
            label      : PropTypes.string,
            description: PropTypes.string,
            // A component, e.g. a lucide icon.
            icon       : PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
            disabled   : PropTypes.bool
        })
    ),
    errors        : PropTypes.object,
    label         : PropTypes.string,
    helpText      : PropTypes.string,
    required      : PropTypes.bool,
    columns       : PropTypes.oneOf([1, 2, 3]),
    disabled      : PropTypes.bool,
    className     : PropTypes.string,
    customOnChange: PropTypes.func
};

export default OptionCardField;
